import { useUploadFile } from '@core/shared';
import { OptionsApi, WebinarDataWithAssets } from '@core/swagger-client';
import { Button, Delete, ErrorMessage, Upload, useFileDropzone } from '@core/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MouseEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

interface Props {
    webinar: WebinarDataWithAssets;
}

export const EditBannerImage = ({ webinar }: Props) => {
    const queryClient = useQueryClient();
    const { upload, progress, isSuccess } = useUploadFile(`/api/webinar/${webinar.webinarId}/banner-image`);
    const { getRootProps, getInputProps, isDragActive, rejectionCode } = useFileDropzone({
        multiple: false,
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length === 0) {
                return;
            }

            const filePayload = new FormData();
            filePayload.append('file', acceptedFiles[0]);
            upload(filePayload).then(() => {
                queryClient.invalidateQueries({ queryKey: ['webinar'] });
                queryClient.invalidateQueries({ queryKey: ['getContentSummary'] });
            });
        },
        accept: {
            'image/png': ['.png'],
            'image/jpg': ['.jpg'],
            'image/webp': ['.webp']
        }
    });

    const deleteBannerImageMutation = useMutation(OptionsApi.webinar.deleteBannerImage());
    const deleteImage = (e: MouseEvent) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        deleteBannerImageMutation.mutate(webinar.webinarId, {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['webinar'] });
                queryClient.invalidateQueries({ queryKey: ['getContentSummary'] });
            }
        });
    };

    const { t } = useTranslation();

    return (
        <>
            <div
                {...getRootProps()}
                className={`group relative w-full min-h-32 ${isDragActive && 'bg-accent-lighter'} hover:bg-accent-lighter focus-visible:bg-accent-lighter rounded-lg flex mb-4 md:mb-6 outline-hidden overflow-hidden transition-colors duration-150 before:pt-[25%] before:float-left after:table after:clear-both`}
            >
                {!webinar.assets.bannerImage && (
                    <div className="flex flex-col items-center m-auto p-2">
                        <Upload classNames="size-12 mb-1 stroke-current group-hover:stroke-primary transition-color duration-200 ease-in-out" />
                        <p className="font-accent font-w-heading text-3xl mb-2">{isDragActive ? t('Generic.DragDrop.Active') : t('Generic.DragDrop')}</p>
                        <p>
                            <Trans components={{ span: <span className="border-b-2 border-primary" /> }} i18nKey="Generic.DragDrop.BrowseFiles" />
                        </p>
                        <p className="mt-4">
                            {t('Generic.DragDrop.AcceptedText')}
                            <span className="font-medium">{t('Generic.DragDrop.AcceptedImageTypes')}</span>
                        </p>
                    </div>
                )}
                {webinar.assets.bannerImage && <img src={webinar.assets.bannerImage.imageUrl} className="absolute inset-0 h-full object-cover" alt="" />}
                <div
                    className={`absolute inset-0 rounded-lg border-2 border-dashed ${!webinar.assets.bannerImage && !isDragActive && 'border-gray-500'} ${webinar.assets.bannerImage && !isDragActive && 'border-transparent'} ${isDragActive && 'border-accent'} group-hover:border-accent group-focus-visible:border-accent transition-colors duration-150`}
                ></div>
                {webinar.assets.bannerImage && (
                    <div className="relative flex gap-4 ms-auto mt-auto p-2">
                        <Button variant="primary-outline" aria-label={t('WebinarDetails.Button.UploadBannerImage.AriaLabel')}>
                            <Upload classNames="size-6 flex-none stroke-current menu:me-2" />
                            <span className="hidden menu:inline">{t('Generic.Button.UploadImage')}</span>
                        </Button>
                        <Button
                            onClick={deleteImage}
                            isLoading={deleteBannerImageMutation.isPending}
                            variant="warning-outline"
                            aria-label={t('WebinarDetails.Button.UseDefaultBannerImage.AriaLabel')}
                        >
                            <Delete classNames="size-6 flex-none stroke-current menu:me-2" />
                            <span className="hidden menu:inline">{t('WebinarDetails.Button.UseDefaultImage')}</span>
                        </Button>
                    </div>
                )}
            </div>
            {rejectionCode && <ErrorMessage errorSubject="Generic.FileRejection." errorCode={rejectionCode} classNames="mt-4" />}
            <input {...getInputProps()} />
        </>
    );
};
